import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Loader2, AlertCircle, GitBranch } from 'lucide-react';
import { Button } from '../ui/button';
import { featuresApi, collectionActionsApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import UpgradeContextModal from '../billing/UpgradeContextModal';

// Import the new split panel component
import FeatureSplitPanel from '../copilot/FeatureSplitPanel';

// =============================================================================
// MAIN SPLIT FEATURE MODAL COMPONENT
// =============================================================================

const SplitFeatureModal = ({
  isOpen,
  originalFeature,
  splitData, // { feature_1: {...}, feature_2: {...}, _meta?: {...} }
  productId: propProductId, // Explicit productId prop (preferred over originalFeature.product_id)
  linkableEntities: propLinkableEntities, // Data passed from parent (epics, releases, personas, tags, teamMembers)
  onClose,
  onSplitComplete
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Use explicit prop, fall back to originalFeature.product_id
  const productId = propProductId || originalFeature?.product_id;
  
  // Use linkableEntities from prop (data flows down from parent)
  // This avoids redundant API calls - parent already has this data loaded
  const linkableEntities = propLinkableEntities || {
    epics: [],
    releases: [],
    personas: [],
    tags: [],
    teamMembers: [],
  };
  
  // Extract logging metadata from splitData (passed by UnifiedActionReview)
  // This follows the same pattern as MergeConfirmationModal
  const splitMeta = splitData?._meta;
  
  // State for both features (requirements are objects with text & is_completed)
  const [feature1, setFeature1] = useState({
    title: '',
    description: '',
    user_story: '',
    requirements: [],
    epic_id: '',
    persona_ids: [],
    tag_ids: [],
  });
  
  const [feature2, setFeature2] = useState({
    title: '',
    description: '',
    user_story: '',
    requirements: [],
    epic_id: '',
    persona_ids: [],
    tag_ids: [],
  });
  
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ feature_1: {}, feature_2: {} });
  const [showProgressWarning, setShowProgressWarning] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureUsage, setFeatureUsage] = useState(null);
  
  // Calculate completed requirements from original feature
  const originalCompletedCount = originalFeature?.requirements?.filter(r => 
    typeof r === 'object' ? r.is_completed : false
  ).length || 0;
  const originalTotalCount = originalFeature?.requirements?.length || 0;
  
  // Normalize requirements to objects with text and is_completed
  const normalizeRequirements = (reqs) => {
    if (!reqs || !Array.isArray(reqs)) return [];
    return reqs.map((r, idx) => {
      if (typeof r === 'string') {
        return { 
          id: `req_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
          text: r, 
          is_completed: false 
        };
      }
      return { 
        id: r.id || `req_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
        text: r?.text || String(r), 
        is_completed: r?.is_completed || false 
      };
    });
  };
  
  // Reset errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({ feature_1: {}, feature_2: {} });
    }
  }, [isOpen]);
  
  // Initialize from splitData
  useEffect(() => {
    if (splitData && isOpen) {
      // Use original feature's ref fields as defaults for both split features
      const defaultEpicId = originalFeature?.epic_id || '';
      const defaultPersonaIds = originalFeature?.persona_ids || [];
      const defaultTagIds = originalFeature?.tag_ids || [];
      
      if (splitData.feature_1) {
        setFeature1({
          title: splitData.feature_1.title || '',
          description: splitData.feature_1.description || '',
          user_story: splitData.feature_1.user_story || '',
          requirements: normalizeRequirements(splitData.feature_1.requirements),
          epic_id: splitData.feature_1.epic_id || defaultEpicId,
          persona_ids: splitData.feature_1.persona_ids || defaultPersonaIds,
          tag_ids: splitData.feature_1.tag_ids || defaultTagIds,
        });
      }
      if (splitData.feature_2) {
        setFeature2({
          title: splitData.feature_2.title || '',
          description: splitData.feature_2.description || '',
          user_story: splitData.feature_2.user_story || '',
          requirements: normalizeRequirements(splitData.feature_2.requirements),
          epic_id: splitData.feature_2.epic_id || defaultEpicId,
          persona_ids: splitData.feature_2.persona_ids || defaultPersonaIds,
          tag_ids: splitData.feature_2.tag_ids || defaultTagIds,
        });
      }
      setErrors({ feature_1: {}, feature_2: {} });
    }
  }, [splitData, isOpen, originalFeature]);
  
  // ==========================================================================
  // FIELD CHANGE HANDLERS
  // ==========================================================================
  
  const handleFeature1Change = useCallback((field, value) => {
    setFeature1(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors.feature_1[field]) {
      setErrors(prev => ({
        ...prev,
        feature_1: { ...prev.feature_1, [field]: undefined }
      }));
    }
  }, [errors.feature_1]);
  
  const handleFeature2Change = useCallback((field, value) => {
    setFeature2(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors.feature_2[field]) {
      setErrors(prev => ({
        ...prev,
        feature_2: { ...prev.feature_2, [field]: undefined }
      }));
    }
  }, [errors.feature_2]);
  
  // ==========================================================================
  // VALIDATION
  // ==========================================================================
  
  const validateFeatures = () => {
    const newErrors = { feature_1: {}, feature_2: {} };
    let isValid = true;
    
    // Feature 1 validation
    if (!feature1.title?.trim()) {
      newErrors.feature_1.title = 'Title is required';
      isValid = false;
    }
    
    // Feature 2 validation
    if (!feature2.title?.trim()) {
      newErrors.feature_2.title = 'Title is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  // ==========================================================================
  // SPLIT HANDLERS
  // ==========================================================================
  
  // Check if user needs to confirm losing progress
  const handleSplitClick = () => {
    if (!validateFeatures()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (originalCompletedCount > 0 && !showProgressWarning) {
      setShowProgressWarning(true);
      return;
    }
    handleSplit();
  };
  
  // Handle saving both features (actual split)
  const handleSplit = async () => {
    if (!originalFeature?.feature_id) {
      toast.error('Original feature not found');
      return;
    }
    
    setShowProgressWarning(false);
    setSaving(true);
    
    try {
      // Check feature limit first - split creates 1 net new feature (2 new - 1 deleted)
      try {
        const usageResponse = await featuresApi.getUsage();
        const usage = usageResponse.data;
        
        // Check if adding 1 feature (net) would exceed limit
        if (usage.limit && usage.remaining < 1) {
          setFeatureUsage(usage);
          setShowUpgradeModal(true);
          setSaving(false);
          return;
        }
      } catch (usageErr) {
        console.warn('Could not check feature limit:', usageErr);
        // Continue with split if limit check fails
      }
      
      // Call the split endpoint
      const response = await featuresApi.split(originalFeature.feature_id, {
        feature_1: {
          ...feature1,
          requirements: feature1.requirements
        },
        feature_2: {
          ...feature2,
          requirements: feature2.requirements
        }
      });
      
      // Show informative toast about where to find the new features
      const newFeatures = response.data.new_features;
      toast.success(
        `Feature split into "${newFeatures[0]?.title}" and "${newFeatures[1]?.title}". Both are now in the Backlog.`,
        { duration: 5000 }
      );
      
      // Log action outcome if we have the metadata (from copilot flow)
      // This follows the MergeConfirmationModal pattern - only log on success
      if (splitMeta?.logActionOutcome) {
        const entityType = splitMeta.entityType || 'feature';
        splitMeta.logActionOutcome('accepted', `Split ${entityType}`, {
          author_id: splitMeta.user?.user_id,
          author_name: splitMeta.user?.name || splitMeta.user?.email || 'You',
        }, { actionIndex: splitMeta.actionIndex });
      }
      
      // Notify parent and close after a brief delay
      setTimeout(() => {
        onSplitComplete?.(newFeatures, response.data.release_id);
        onClose();
      }, 500);
      
    } catch (err) {
      console.error('Split error:', err);
      toast.error(err.response?.data?.detail || 'Failed to split feature');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    setErrors({ feature_1: {}, feature_2: {} });
    onClose();
  };
  
  if (!isOpen) return null;
  
  const isValid = feature1.title?.trim() && feature2.title?.trim();
  
  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70" onClick={handleCancel} />
      
      {/* Modal */}
      <div className={cn(
        "fixed inset-4 md:inset-8 lg:inset-12 rounded-xl shadow-2xl flex flex-col overflow-hidden",
        colors.bgCard
      )}>
        {/* Header */}
        <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", colors.accentGradient)}>
              <GitBranch className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                Split Feature
              </h2>
              <p className={cn("text-sm", colors.textMuted)}>
                Splitting: {originalFeature?.title}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className={cn("p-2 rounded-lg transition-colors", colors.bgHover)}
          >
            <X className={cn("w-5 h-5", colors.textMuted)} />
          </button>
        </div>
        
        {/* Content - Split Panel */}
        <div className="flex-1 overflow-hidden">
          <FeatureSplitPanel
            originalFeature={originalFeature}
            feature1Data={feature1}
            feature2Data={feature2}
            onFeature1Change={handleFeature1Change}
            onFeature2Change={handleFeature2Change}
            linkableEntities={linkableEntities}
            errors={errors}
          />
        </div>
        
        {/* Footer */}
        <div className={cn("flex items-center justify-between px-6 py-4 border-t", colors.borderPrimary)}>
          <p className={cn("text-sm", colors.textMuted)}>
            The original feature will be deleted after both new features are saved.
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
              className={cn("border", colors.borderPrimary)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSplitClick}
              disabled={!isValid || saving}
              className={cn(colors.accentGradient, "text-white")}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Splitting...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Split Feature
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Progress Warning Dialog */}
      {showProgressWarning && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 rounded-xl">
          <div className={cn("w-full max-w-md rounded-xl shadow-2xl p-6 m-4", colors.bgCard)}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-amber-500/10">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
              <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Requirements Have Progress</h3>
            </div>
            
            <p className={cn("mb-4", colors.textSecondary)}>
              <strong>{originalCompletedCount}</strong> of <strong>{originalTotalCount}</strong> requirements are marked complete on the original feature.
            </p>
            <p className={cn("mb-6", colors.textSecondary)}>
              Splitting will <strong>reset all requirement progress</strong> since the AI has rewritten them. Do you want to continue?
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowProgressWarning(false)}
                className={cn("border", colors.borderPrimary)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSplit}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Continue & Lose Progress
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Feature Limit Upgrade Modal */}
      <UpgradeContextModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="features"
        currentUsage={featureUsage?.used}
        currentLimit={featureUsage?.limit}
        returnPath={window.location.pathname}
        onUpgradeComplete={() => {
          setShowUpgradeModal(false);
          // Retry the split after upgrade
          handleSplit();
        }}
      />
    </div>
  );
};

export default SplitFeatureModal;
